import { omit } from 'lodash';
import { isRecord } from 'utils/objectUtils';
import {
  ruleApplyMetadataSchema,
  AssertionField,
  supportsTariffExclusions,
  RuleMode,
  ScopeMode,
  WhenLogic,
} from '@billing/validation/rules';
import type {
  Rule,
  RuleApplyBehavior,
  RuleApplyNotification,
  RuleApplyMetadata,
  RuleSeverity,
  RuleCriteriaGroup,
} from '@billing/validation/rules';
import {
  RULE_MESSAGE_MAX_LENGTH,
  TIME_BASED_INTERVALS,
  timeBasedApplyFields,
} from './constants';
import type { RuleCondition } from './constants';
import type {
  ApplyUpdateRow,
  AssertionRow,
  RuleModeSelection,
  WhenClause,
} from './ruleSchemas';
import {
  getBodyApplyPayload,
  getBodyEventParams,
  getInitialApplyUpdates,
  getInitialApplyIntervalMinutes,
  getInitialRuleModeSelection,
  getInitialSeverity,
  getInitialRuleCriteria,
  getInitialGroupedCriteria,
  buildValidationFailureCondition,
  createEmptyWhenClause,
} from './ruleUtils';

/** Editable values and row counters owned by useRuleBuilder for create and edit forms. */
export interface RuleBuilderState {
  /** Name entered in the form; the builder generates a fallback when it is blank. */
  ruleName: string;
  /** Alert or block severity serialized for validation rules. */
  severity: RuleSeverity;
  /** Selected action, used to choose outcome controls and build the rule event. */
  ruleMode: RuleModeSelection;
  /** Whether all or any requirement groups must match for the rule to run. */
  whenLogic: WhenLogic;
  /** Editable requirement groups converted into matching conditions. */
  whenGroups: RuleCriteriaGroup[];
  /** Next unused requirement group ID, incremented when a group is added. */
  nextWhenGroupID: number;
  /** Next unused When row ID, incremented when the builder adds a clause. */
  nextWhenClauseID: number;
  /** Legacy validation check rows restored only when a saved rule contains them. */
  assertions: AssertionRow[];
  /** Next unused validation check ID, incremented when a check is added. */
  nextAssertionID: number;

  /** Whether the saved rule can be represented by the form without guessing its criteria. */
  isFormSupported: boolean;
  /** User-facing message serialized into an alert or block rule's event. */
  ruleMessage: string;
  /** Flag title entered in the form; a blank title falls back to the rule name. */
  flagLabel: string;
  /** Flag description entered in the form; the builder supplies a fallback if blank. */
  flagMessage: string;
  /** Display severity serialized with a flag-only rule's event. */
  flagSeverity: string;

  /** Target fields and values serialized into an apply-data rule's updates. */
  applyUpdates: ApplyUpdateRow[];
  applyBehavior: RuleApplyBehavior;
  applyNotification: RuleApplyNotification;
  /** Next unused apply-data row ID, incremented when an update is added. */
  nextApplyUpdateID: number;
  /** Minutes per unit selected for time-based service or anesthesia updates. */
  applyIntervalMinutes: string;
  /** Apply-data priority kept as input text and parsed when building the event. */
  applyPriority: string;

  /** Tariff-transform priority kept as input text and parsed for the event. */
  transformPriority: string;
  /** Enables appending time flags to provider notes in the transform payload. */
  transformAppendFlags: boolean;
  /** Enables combining tariffs with the same code in the transform payload. */
  transformCollapseByCode: boolean;
  /** Enables applying base tariff percentages in the transform payload. */
  transformApplyPercentages: boolean;
  /** After-midnight tariff code serialized into the transform's time-code mapping. */
  timeCodeAfterMidnight: string;
  /** Evening tariff code serialized into the transform's time-code mapping. */
  timeCodeEvening: string;
  /** Weekend daytime tariff code serialized into the transform's time-code mapping. */
  timeCodeWeekend: string;
}

/** Derived builder values needed to validate the selected outcome. */
interface RuleOutcomeValidation {
  /** Flag title after applying the rule-name fallback, used to require a title. */
  effectiveFlagLabel: string;
  /** Positive exact tariff matches required to validate a billing-history check. */
  exactTariffValues: string[];
  /** Generated check-failure conditions; at least one is required for legacy checks. */
  validationFailureConditions: RuleCondition[];
}

const getNextRowID = (rows: readonly { id?: number }[]): number =>
  rows.reduce((highestID, row) => Math.max(highestID, row.id ?? 0), 0) + 1;

// Added conditions start incomplete so they cannot silently broaden a rule.
export const createEmptyCriteriaClause = (clauseID: number): WhenClause => ({
  ...createEmptyWhenClause(clauseID),
  scopeMode: ScopeMode.ExactTariff,
});

/** Applies an edit to the selected group; omitted IDs allow legacy clause-only callers. */
const updateRuleCriteriaGroup = (
  current: RuleBuilderState,
  groupID: number | undefined,
  update: (group: RuleCriteriaGroup) => RuleCriteriaGroup
): RuleBuilderState => ({
  ...current,
  whenGroups: current.whenGroups.map((group) => {
    if (groupID !== undefined && group.id !== groupID) return group;
    return update(group);
  }),
});

/** Patches a criteria row by ID without replacing its identity or other rows. */
export const updateRuleCriteriaClause = (
  current: RuleBuilderState,
  rowID: number,
  update: Partial<Omit<WhenClause, 'id'>>,
  groupID?: number
): RuleBuilderState => {
  return updateRuleCriteriaGroup(current, groupID, (group) => ({
    ...group,
    clauses: group.clauses.map((clause) => {
      if (clause.id !== rowID) return clause;
      const next = { ...clause, ...update };
      return {
        ...next,
        excludedTariffValuesText: supportsTariffExclusions(
          next.scopeMode,
          next.whenOperator
        )
          ? next.excludedTariffValuesText
          : '',
      };
    }),
  }));
};

/** Appends an incomplete condition and reserves its ID within the selected group. */
export const addRuleCriteriaClause = (
  current: RuleBuilderState,
  groupID?: number
): RuleBuilderState => {
  return {
    ...current,
    whenGroups: current.whenGroups.map((group) =>
      group.id === (groupID ?? current.whenGroups[0]?.id)
        ? {
            ...group,
            clauses: [
              ...group.clauses,
              createEmptyCriteriaClause(current.nextWhenClauseID),
            ],
          }
        : group
    ),
    nextWhenClauseID: current.nextWhenClauseID + 1,
  };
};

/** Removes a condition by ID while retaining at least one condition in its group. */
export const removeRuleCriteriaClause = (
  current: RuleBuilderState,
  rowID: number,
  groupID?: number
): RuleBuilderState => {
  return updateRuleCriteriaGroup(current, groupID, (group) => {
    if (group.clauses.length === 1) return group;
    return {
      ...group,
      clauses: group.clauses.filter((clause) => clause.id !== rowID),
    };
  });
};

/** Adds a requirement using OR between its conditions and reserves both new IDs. */
export const addRuleCriteriaGroup = (
  current: RuleBuilderState
): RuleBuilderState => {
  return {
    ...current,
    whenGroups: [
      ...current.whenGroups,
      {
        id: current.nextWhenGroupID,
        operator: WhenLogic.Any,
        clauses: [createEmptyCriteriaClause(current.nextWhenClauseID)],
      },
    ],
    nextWhenGroupID: current.nextWhenGroupID + 1,
    nextWhenClauseID: current.nextWhenClauseID + 1,
  };
};

/** Removes a requirement while retaining at least one group. */
export const removeRuleCriteriaGroup = (
  current: RuleBuilderState,
  groupID: number
): RuleBuilderState => {
  if (current.whenGroups.length === 1) return current;
  return {
    ...current,
    whenGroups: current.whenGroups.filter((group) => group.id !== groupID),
  };
};

/** Changes the operator within a requirement independently of the root operator. */
export const setRuleCriteriaGroupLogic = (
  current: RuleBuilderState,
  groupID: number,
  operator: WhenLogic
): RuleBuilderState => {
  return updateRuleCriteriaGroup(current, groupID, (group) => ({
    ...group,
    operator,
  }));
};

/**
 * Initializes useRuleBuilder state from a saved body or defaults for a new rule.
 * Bodies without supported builder metadata retain their original logic outside the form.
 *
 * @param initialBody Saved rule body to restore, if editing an existing rule.
 * @param initialName Saved name to populate the name input.
 */
export const createRuleBuilderState = (
  initialBody?: Rule['body'],
  initialName = ''
): RuleBuilderState => {
  const { assertions: initialAssertions } = getInitialRuleCriteria(initialBody);
  const initialEventParams = getBodyEventParams(initialBody);
  const initialApplyPayload = getBodyApplyPayload(initialBody);
  const initialApplyUpdates = getInitialApplyUpdates(initialBody);
  const initialMode = getInitialRuleModeSelection(initialBody);
  const hasSavedApplication =
    Boolean(initialBody) && initialMode === RuleMode.ApplyData;
  const applyMetadata = ruleApplyMetadataSchema.safeParse(
    hasSavedApplication ? initialApplyPayload : {}
  );
  const savedMetadata = applyMetadata.success ? applyMetadata.data : {};
  const applySettings: Required<RuleApplyMetadata> = {
    behavior: savedMetadata.behavior ?? 'automatic',
    notification:
      savedMetadata.notification ?? (hasSavedApplication ? 'silent' : 'notice'),
    writePolicy: hasSavedApplication
      ? savedMetadata.writePolicy ?? 'replace'
      : 'blank',
  };
  const unsupportedUpdateMetadata =
    hasSavedApplication &&
    Array.isArray(initialApplyPayload.updates) &&
    initialApplyPayload.updates.some((update) => {
      if (!isRecord(update)) return false;
      const metadata = ruleApplyMetadataSchema.safeParse(update);
      if (!metadata.success) return true;
      return (
        (metadata.data.behavior ?? applySettings.behavior) !==
          applySettings.behavior ||
        (metadata.data.notification ?? applySettings.notification) !==
          applySettings.notification
      );
    });

  const criteria = getInitialGroupedCriteria(initialBody);
  const whenGroups = criteria?.groups ?? [];
  const isUnsupported =
    !criteria ||
    !applyMetadata.success ||
    unsupportedUpdateMetadata ||
    Boolean(
      initialBody &&
        !(initialBody.when || initialBody.tariff || initialBody.checks)
    );
  const initialTimeCodes = isRecord(initialApplyPayload.timeCodes)
    ? initialApplyPayload.timeCodes
    : {};
  return {
    ruleName: initialName,
    severity: getInitialSeverity(initialBody),
    ruleMode: initialMode,
    whenLogic: criteria?.operator ?? WhenLogic.All,
    whenGroups,
    nextWhenGroupID: getNextRowID(whenGroups),
    nextWhenClauseID: getNextRowID(
      whenGroups.flatMap((group) => group.clauses)
    ),
    assertions: initialAssertions,
    nextAssertionID: getNextRowID(initialAssertions),
    isFormSupported: !isUnsupported,
    applyBehavior: applySettings.behavior,
    applyNotification: applySettings.notification,
    applyUpdates: initialApplyUpdates.map((update) => ({
      ...omit(update, ['behavior', 'notification']),
      writePolicy: hasSavedApplication
        ? update.writePolicy ?? applySettings.writePolicy
        : applySettings.writePolicy,
    })),
    nextApplyUpdateID: getNextRowID(initialApplyUpdates),
    ruleMessage:
      typeof initialEventParams.userMessage === 'string'
        ? initialEventParams.userMessage
        : '',
    flagLabel:
      typeof initialEventParams.flagLabel === 'string'
        ? initialEventParams.flagLabel
        : '',
    flagMessage:
      typeof initialEventParams.flagMessage === 'string'
        ? initialEventParams.flagMessage
        : '',
    flagSeverity:
      typeof initialEventParams.flagSeverity === 'string'
        ? initialEventParams.flagSeverity
        : 'Medium',
    applyIntervalMinutes: getInitialApplyIntervalMinutes(initialBody),
    applyPriority:
      typeof initialApplyPayload.priority === 'undefined'
        ? '1'
        : String(initialApplyPayload.priority),
    transformPriority:
      typeof initialApplyPayload.priority === 'undefined'
        ? '1'
        : String(initialApplyPayload.priority),
    transformAppendFlags:
      typeof initialApplyPayload.appendTimeFlagsToProviderNotes === 'boolean'
        ? initialApplyPayload.appendTimeFlagsToProviderNotes
        : true,
    transformCollapseByCode:
      typeof initialApplyPayload.collapseTariffsByCode === 'boolean'
        ? initialApplyPayload.collapseTariffsByCode
        : true,
    transformApplyPercentages:
      typeof initialApplyPayload.applyBaseTariffPercentages === 'boolean'
        ? initialApplyPayload.applyBaseTariffPercentages
        : true,
    timeCodeAfterMidnight:
      typeof initialTimeCodes.afterMidnight === 'string'
        ? initialTimeCodes.afterMidnight
        : '75553',
    timeCodeEvening:
      typeof initialTimeCodes.evening === 'string'
        ? initialTimeCodes.evening
        : '75555',
    timeCodeWeekend:
      typeof initialTimeCodes.weekendDaytime === 'string'
        ? initialTimeCodes.weekendDaytime
        : '75550',
  };
};

/**
 * Selects the active outcome's editable values for section change detection.
 *
 * @param state Current builder state to compare with the initial edit snapshot.
 */
export const getRuleOutcomeSnapshot = (
  state: RuleBuilderState
): Record<string, unknown> => {
  switch (state.ruleMode) {
    case RuleMode.ApplyData:
      return {
        applyUpdates: state.applyUpdates,
        applyBehavior: state.applyBehavior,
        applyNotification: state.applyNotification,
        applyIntervalMinutes: state.applyIntervalMinutes,
        applyPriority: state.applyPriority,
      };
    case RuleMode.TariffTransforms:
      return {
        transformPriority: state.transformPriority,
        transformAppendFlags: state.transformAppendFlags,
        transformCollapseByCode: state.transformCollapseByCode,
        transformApplyPercentages: state.transformApplyPercentages,
        timeCodeAfterMidnight: state.timeCodeAfterMidnight,
        timeCodeEvening: state.timeCodeEvening,
        timeCodeWeekend: state.timeCodeWeekend,
      };
    case 'flagOnly':
      return {
        flagLabel: state.flagLabel,
        flagMessage: state.flagMessage,
        flagSeverity: state.flagSeverity,
      };
    default:
      return { ruleMessage: state.ruleMessage };
  }
};

/**
 * Uses the shared compiler to validate old history assertions that have not been migrated.
 *
 * @param assertion Check row being validated before the outcome can be saved.
 * @param exactTariffValues Positive exact tariff matches from the When clauses.
 */
const isBillingHistoryCheckValid = (
  assertion: AssertionRow,
  exactTariffValues: string[]
): boolean => {
  if (assertion.field !== AssertionField.TariffBillingHistory) return true;
  return buildValidationFailureCondition(assertion, exactTariffValues) !== null;
};

/**
 * Checks required inputs for the selected outcome, including legacy validation
 * checks. useRuleBuilder combines this with other checks to enable saving.
 *
 * @param state Current outcome inputs and selected rule mode.
 * @param validation Derived flags, fallback values, and generated check conditions.
 */
export const isRuleOutcomeValid = (
  state: RuleBuilderState,
  validation: RuleOutcomeValidation
): boolean => {
  switch (state.ruleMode) {
    case RuleMode.ApplyData:
      return (
        state.applyUpdates.length > 0 &&
        state.applyUpdates.every((update) =>
          timeBasedApplyFields.has(update.field)
            ? TIME_BASED_INTERVALS.some(
                (interval) => interval === state.applyIntervalMinutes
              )
            : Boolean(update.value.trim())
        )
      );
    case RuleMode.TariffTransforms:
      return Boolean(
        state.timeCodeAfterMidnight.trim() &&
          state.timeCodeEvening.trim() &&
          state.timeCodeWeekend.trim()
      );
    case 'flagOnly':
      return Boolean(validation.effectiveFlagLabel.trim());
    default:
      if (state.assertions.length === 0) {
        return (
          Boolean(state.ruleMessage.trim()) &&
          state.ruleMessage.length <= RULE_MESSAGE_MAX_LENGTH
        );
      }
      return (
        validation.validationFailureConditions.length > 0 &&
        state.assertions.every((assertion) =>
          isBillingHistoryCheckValid(assertion, validation.exactTariffValues)
        )
      );
  }
};
